import React, { useState, useEffect } from "react";
import API_ENDPOINTS from "../apiConfig";
import { useTranslation } from "react-i18next";
import Notification from "./Notification";
import ConfirmModal from "./ConfirmModal";
import { authFetch } from "../utils/authFetch";
import { useSite } from "../context/SiteContext";
import { useNavigate, useLocation } from "react-router-dom";


function UserManagement({ fixedScopeType, fixedScopeId, selectedSite, compact = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [name, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [scopeType, setScopeType] = useState(fixedScopeType || "site");
  const [selectedAreaLocal, setSelectedAreaLocal] = useState(fixedScopeId || "");
  const [gdprConsent, setGdprConsent] = useState(false);
  const { areas } = useSite();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [userToDelete, setUserToDelete] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showHandoffHint, setShowHandoffHint] = useState(
    !compact && !!(location.state?.email || location.state?.name || location.state?.role)
  );


  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.name : "";
  };
  const getAreaNames = (areaIds) =>
    areaIds.map(id => getAreaName(id)).filter(Boolean);

  // Fetch users list
  const fetchUsers = async () => {
    try {
      const siteId = selectedSite.id;
      const res = await authFetch(API_ENDPOINTS.getusers.replace("##siteid##", siteId));
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      console.error(err);
      setErrorMessage(t("Failed to load users"));
    }
  };

  useEffect(() => {
    if (!selectedSite?.id) return;
    fetchUsers();
  }, [selectedSite?.id]);

  useEffect(() => {
    if (!compact && location.state) {
      if (location.state.email) setEmail(location.state.email);
      if (location.state.name)  setUsername(location.state.name);
      if (location.state.role)  setRole(location.state.role);
    }
  }, []);

  // group users here when more areas
  const groupedUsers = React.useMemo(() => {
    const map = {};

    users.forEach(u => {
      if (!map[u.id]) {
        map[u.id] = {
          id: u.id,
          name: u.name,
          email: u.email,
          scopes: [], // 👈 ALL scopes go here
        };
      }

      if (u.scope === "area" && u.area_id) {
        const area = areas.find(a => a.id === u.area_id);
        if (area) {
          map[u.id].scopes.push({
            type: "area",
            id: area.id,
            label: area.name,
            role: u.rolename,
          });
        }
      } else {
        // global / partner / site
        map[u.id].scopes.push({
          type: u.scope,
          id: null,
          label: u.scopeName || u.scope,
          role: u.rolename,
        });
      }
    });

    return Object.values(map);
  }, [users, areas]);



  const groupedUserList = Object.values(groupedUsers);
  //end group users

  const roleSummary = React.useMemo(() => {
    const counts = {};
    users.forEach(u => {
      if (u.scope === "site" || u.scope === "area") {
        counts[u.rolename] = (counts[u.rolename] || 0) + 1;
      }
    });
    return counts;
  }, [users]);

  const handleDelete = async ({ userId, scopeType, scopeId }) => {
    let endpoint;

    switch (scopeType) {
      case "area":
        endpoint = API_ENDPOINTS.getusersarea.replace(
          "##areaid##",
          scopeId
        );
        break;

      case "site":
        endpoint = API_ENDPOINTS.getusers.replace(
          "##siteid##",
          selectedSite.id
        );
        break;

      default:
        return; // global / partner not deletable
    }

    await authFetch(`${endpoint}/${userId}`, { method: "DELETE" });

    fetchUsers();
  };

  const handleCreate = async () => {
    if (!email) return setErrorMessage(t("Please enter an email"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setErrorMessage(t("Please enter a valid email address"));

    if (scopeType === "area" && !selectedAreaLocal) {
      return setErrorMessage(t("Please select an area for the user"));
    }

    if (scopeType === "area" && role === "site_manager") {
      return setErrorMessage(t("site_manager for area not allowed"));
    }

    if (!gdprConsent) {
      return setErrorMessage(t("Please confirm you have the legal basis to add this person."));
    }


    try {
      setLoading(true);
      const body = { email, role, name };

      const siteId = selectedSite.id;
      let endpoint = API_ENDPOINTS.getusers.replace("##siteid##", siteId);
      if (selectedAreaLocal && scopeType == "area") {
        endpoint = API_ENDPOINTS.getusersarea.replace("##areaid##", selectedAreaLocal);
      }
      const response = await authFetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Failed to create user");
      }

      setSuccessMessage(t("User created successfully!"));
      setEmail("");
      setUsername("");
      setRole("");
      setGdprConsent(false);
      setShowHandoffHint(false);
      if (!fixedScopeType) {
        setScopeType("site");
        setSelectedAreaLocal("");
      }

      fetchUsers(); // refresh list after creation
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };
  const filteredUsers = React.useMemo(() => {
    if (!searchTerm) return groupedUsers;

    const term = searchTerm.toLowerCase();

    return groupedUsers.filter(u =>
      u.name?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term)
    );
  }, [groupedUsers, searchTerm]);


  return (
    <div className="p-4 border ez-rounded-card bg-white shadow-sm">
      {compact ? (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">{t("Users")}</h4>
          {localStorage.getItem("actualUserRole") !== "gate_opener" && (
            <button
              className="btn btn-sm btn-light"
              onClick={() => navigate('/site-settings?tab=users')}
              title={t("Manage users")}
            >
              <i className="bi bi-people me-1"></i>{t("Manage users")}
            </button>
          )}
        </div>
      ) : (
        <>
          <p className="text-muted small mb-3">
            {t("Enter an email address and role to grant someone access to this site.")}
          </p>
          {showHandoffHint && (
            <div className="alert alert-info py-2 mb-3 d-flex align-items-center gap-2" role="alert">
              <i className="bi bi-info-circle flex-shrink-0"></i>
              <span className="flex-grow-1">{t("Details carried over from the dashboard — review and click Grant Authorization to complete.")}</span>
              <button type="button" className="btn-close flex-shrink-0" onClick={() => setShowHandoffHint(false)} />
            </div>
          )}
        </>
      )}

      {/* Compact summary */}
      {compact && users.length > 0 && (
        <p className="text-muted small mb-3">
          {Object.entries(roleSummary).sort(([a], [b]) => {
            const order = ['site_manager', 'gate_opener'];
            return (order.indexOf(a) ?? 99) - (order.indexOf(b) ?? 99);
          }).map(([role, count], i) => (
            <span key={role}>
              {i > 0 && ", "}
              <strong>{count}</strong> {t(role)}
            </span>
          ))}
        </p>
      )}

      {/* Email + Name inputs */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("Email")}</label>
          <input
            type="email"
            className="form-control form-control-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("Enter email")}

          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("Name")}</label>
          <input
            type="text"
            className="form-control form-control-sm"
            value={name}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={t("Enter user's name")}
          />
        </div>
      </div>

      {/* Role + Scope row */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">{t("Role")}</label>
          <select
            className="form-select form-select-sm"
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              if (e.target.value === "site_manager") setScopeType("site");
            }}
          >
            <option value=""></option>
            <option value="site_manager">{t("site_manager")}</option>
            <option value="gate_opener">{t("gate_opener")}</option>
          </select>
        </div>

        {!fixedScopeType && !compact && (
          <div className="col-md-6 mb-3">
            <label className="form-label">{t("Scope")}</label>
            <div className="d-flex align-items-center gap-3 mt-1">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="scope"
                  value="site"
                  id="scopeSite"
                  checked={scopeType === "site"}
                  onChange={() => setScopeType("site")}
                />
                <label className="form-check-label" htmlFor="scopeSite">
                  {t("Site")}
                </label>
              </div>
              {role !== "site_manager" && (
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="scope"
                  value="area"
                  id="scopeArea"
                  checked={scopeType === "area"}
                  onChange={() => setScopeType("area")}
                />
                <label className="form-check-label" htmlFor="scopeArea">
                  {t("Area")}
                </label>
              </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Area dropdown (if needed) */}
      {!fixedScopeId && scopeType === "area" && !compact && (
        <div className="mb-3">
          <label className="form-label">{t("select_area")}</label>
          <select
            className="form-select form-select-sm"
            value={selectedAreaLocal}
            onChange={(e) => setSelectedAreaLocal(e.target.value)}
          >
            <option value="">-- {t("select_area")} --</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* GDPR consent checkbox (full mode only) */}
      {!compact && (
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="gdprConsent"
            checked={gdprConsent}
            onChange={(e) => setGdprConsent(e.target.checked)}
          />
          <label className="form-check-label small text-muted" htmlFor="gdprConsent">
            {t("I confirm I have the legal basis to add this person to the system.")}
          </label>

        </div>
      )}

      {/* Submit button */}
      {compact ? (
        localStorage.getItem("actualUserRole") !== "gate_opener" && (
        <button
          className="btn btn-sm ez-button-full w-100"
          onClick={() => navigate('/site-settings?tab=users', { state: { email, name, role } })}
        >
          {t("Grant Authorization")} <i className="bi bi-arrow-right ms-1"></i>
        </button>
        )
      ) : (
        <button
          className="btn btn-sm ez-button-full"
          onClick={handleCreate}
          disabled={loading}
        >
          {loading ? t("Creating...") : t("Grant Authorization")}
        </button>
      )}

      <Notification
        message={errorMessage}
        type="danger"
        onClose={() => setErrorMessage("")}
      />
      <Notification
        message={successMessage}
        type="success"
        onClose={() => setSuccessMessage("")}
      />

      {/* Users list (full mode only) */}
      {!compact && (
        <>
          <hr className="my-4" />
          <h5>{t("Existing Users")}</h5>
          <div className="input-group mb-3">
            <span className="input-group-text" id="search-icon">
              <i className="bi bi-search"></i>
            </span>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder={t("Search by name or email")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="btn btn-outline-secondary" onClick={() => setSearchTerm("")}>
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>
          {users.length === 0 ? (
            <p className="text-muted">{t("No users found")}</p>
          ) : (
            <ul className="list-group">
              {filteredUsers.map(user => (
                <li key={user.id} className="list-group-item">

                  {/* USER HEADER */}
                  <div className="mb-2">
                    <strong>{user.name}</strong><br />
                    <small>{user.email}</small>
                  </div>

                  {/* SCOPES */}
                  <ul className="list-group list-group-flush">
                    {user.scopes.map((scope, index) => (
                      <li
                        key={`${scope.type}-${scope.id ?? index}`}
                        className="list-group-item d-flex justify-content-between align-items-center py-1"
                      >
                        <div>
                          <span className="badge bg-light text-dark me-2">
                            {scope.label}
                          </span>
                          <span className="text-muted small">
                            {t("Role")}: {scope.role}
                          </span>
                        </div>

                        {scope.type !== "global" && scope.type !== "partner" && (
                          <button
                            className="btn btn-sm ez-button-light"
                            onClick={() => {
                              setUserToDelete({ userId: user.id, scopeType: scope.type, scopeId: scope.id, user: user.email });
                              setShowConfirm(true);
                            }}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
          {/* Confirm delete modal */}
          <ConfirmModal
            show={showConfirm}
            title={t("Confirm_Deletion")}
            onCancel={() => {
              setShowConfirm(false);
              setUserToDelete(null);
            }}
            onConfirm={async () => {
              await handleDelete(userToDelete);
              setShowConfirm(false);
              setUserToDelete(null);
            }}
            confirmText={t("Delete")}
            cancelText={t("Cancel")}
          >
            <p>
              {t("Delete_question_user")}{" "}
              <strong>{userToDelete?.user}</strong>?
            </p>
          </ConfirmModal>
        </>
      )}
    </div>
  );
}

export default UserManagement;
